import type { PageGuide } from "../page-guide.js";

/**
 * Help guide for the Document Vault page.
 *
 * Anchor ids here must match the `data-help-anchor` attributes on the
 * corresponding panels in the documents surface.
 */
export const documentsGuide: PageGuide = {
  route: "/documents",
  version: 4,
  overviewArticleId: "vault-overview",
  steps: [
    {
      id: "vault-intro",
      title: "Three modes on Documents",
      body: "Pick the **Organisation** first (defaults to **Latitude30**). Day-to-day areas such as Internal or Suppliers are **Vault folders**, not separate organisations. Use **Vault** for finals and templates, **Working** for drafts on SharePoint, and **Agreements** for the lifecycle dossier.",
    },
    {
      id: "working-prepare",
      title: "Prepare a negotiated Word for signature",
      body: "On **Working**, **Upload** the counsel file (Team site attached), then **Prepare for signature**. Classify paragraphs (Exclude / Recitals / Operative / Schedules) — Word list letters stay as Word wrote them — then create the L30 shell. The success step offers **Open in Word** and **Promote to Vault**; then fields and send.",
      anchorId: "prepare-for-signature",
    },
    {
      id: "share-with-client",
      title: "Share a document with the client",
      body: "Use **Share with client** to publish a working draft or finalised document to a client's portal — pick **Comment** for feedback or **Read** for reference.",
      anchorId: "share-with-client",
    },
  ],
  panels: [
    {
      anchorId: "prepare-for-signature",
      title: "Prepare for signature",
      body: "Creates a **new** working Word file in the Latitude30 shell. Classify source paragraphs into Recitals / Operative / Schedules (or Exclude cover and signature pages). List markers are taken from Word numbering, not rewritten by AI. After create, **Open in Word** and/or **Promote to Vault** from the same dialog, then Edit fields → Send.",
      relatedArticleId: "agreement-lifecycle",
    },
    {
      anchorId: "share-with-client",
      title: "Share with client",
      body: "Publishes this document to a client's portal. Choose **Comment** to let them leave feedback, or **Read** for reference only. Working drafts share the current version — re-share to push a newer one. Un-share is reversible.",
      relatedArticleId: "sharing-documents-with-clients",
    },
  ],
  guidedActions: [
    {
      id: "prepare-first-signing-draft",
      label: "Prepare a document for signature",
      description:
        "Classify paragraphs from a negotiated Word file into an L30 signing shell (list markers from Word).",
      when: (ctx) => (ctx.counts.documents ?? 0) > 0,
      actionAnchorId: "prepare-for-signature",
      requiresRole: ["admin", "exec", "sales"],
    },
    {
      id: "share-first-document",
      label: "Share a document with the client",
      description: "Publish a draft or final document to the client's portal for review.",
      when: (ctx) => (ctx.counts.documents ?? 0) > 0 && (ctx.counts.sharedDocuments ?? 0) === 0,
      actionAnchorId: "share-with-client",
      requiresRole: ["admin", "exec"],
    },
  ],
};
